import {
  getReleaseRiderUrl,
  computeNameChangeOk,
  isNameChangeEndorsement,
} from "../../Law Firm User/helpers/lawFirmBondsHelpers";

/**
 * Shared multipart payload slice for POST /endorse-bond/.
 * Matches {@link buildEndorsementFormData} consumption.
 */
export function buildEndorsementPayloadFromState({
  endorsementType,
  endorsementFile,
  contactName,
  contactEmail,
  newBondAmount,
  selectedBondToEndorse,
  estimatedAssets,
  endorsementToCounty,
  updatedName,
  dwbFirstName,
  dwbMiddleName,
  dwbLastName,
  dwbSuffix,
  dwbAlias,
  endorsementPrincipalId,
}) {
  return {
    endorsementType,
    endorsementFile,
    contactName,
    contactEmail,
    newBondAmount,
    selectedBondToEndorse,
    estimatedAssets,
    endorsementToCounty,
    updatedName,
    dwbFirstName,
    dwbMiddleName,
    dwbLastName,
    dwbSuffix,
    dwbAlias,
    endorsementPrincipalId,
  };
}

/**
 * Validates endorsement submit parity (Approve Bonds · Law Firm · Archives callers).
 * @returns {{ ok: true } | { ok: false, toastMessage: string }}
 */
export function validateEndorsementSubmit({
  selectedBondToEndorse,
  endorsementType,
  endorsementFile,
  contactName,
  contactEmail,
  newBondAmount,
  endorsementToCounty,
  updatedName,
  dwbFirstName,
  dwbLastName,
}) {
  const nameChangeOk = computeNameChangeOk({
    endorsementType,
    endorsementToCounty,
    updatedName,
    selectedBondToEndorse,
    dwbFirstName,
    dwbLastName,
  });
  const invalid =
    !selectedBondToEndorse ||
    (!isNameChangeEndorsement(endorsementType) && !endorsementFile) ||
    (isNameChangeEndorsement(endorsementType) ? !nameChangeOk : !newBondAmount) ||
    !endorsementType ||
    !contactName ||
    !contactEmail;
  if (invalid) {
    return {
      ok: false,
      toastMessage: isNameChangeEndorsement(endorsementType)
        ? "Please fill all required fields and contact info."
        : "Please fill all required fields, contact info, and upload the file.",
    };
  }
  return { ok: true };
}

const AMOUNT_ENDORSEMENT_TYPES = new Set(["Increase", "Decrease"]);

const parseEndorsementAmount = (value) => {
  const cleaned = String(value ?? "").replace(/[^0-9.-]+/g, "");
  const num = cleaned ? Number(cleaned) : Number.NaN;
  return Number.isNaN(num) ? null : num;
};

/**
 * When Release Rider navigates without POST (e.g. no court order file), pass draft
 * data so BondRiderForm can prefill Increase/Decrease fields.
 */
export function buildRiderEndorsementDraftFromDialog(endorsementState) {
  const {
    endorsementType,
    newBondAmount,
    selectedBondToEndorse,
  } = endorsementState;
  if (!endorsementType || !selectedBondToEndorse) return null;
  if (!AMOUNT_ENDORSEMENT_TYPES.has(endorsementType) || !newBondAmount) {
    return null;
  }
  const toAmount = parseEndorsementAmount(newBondAmount);
  if (toAmount == null) return null;
  const fromAmount = parseEndorsementAmount(
    selectedBondToEndorse.bond_amount ?? selectedBondToEndorse.bondAmount
  );
  return {
    type: endorsementType,
    fromAmount,
    toAmount,
    endorsedAt: new Date().toISOString(),
  };
}

/** Gates internal/super endorsement POST before navigating to rider doc (parity with legacy hooks). */
export function computeHasEndorsementDetailsForRelease({
  endorsementType,
  contactName,
  contactEmail,
  newBondAmount,
  endorsementFile,
  selectedBondToEndorse,
  endorsementToCounty,
  updatedName,
  dwbFirstName,
  dwbLastName,
}) {
  const nameChangeOk = computeNameChangeOk({
    endorsementType,
    endorsementToCounty,
    updatedName,
    selectedBondToEndorse,
    dwbFirstName,
    dwbLastName,
  });
  return (
    endorsementType &&
    contactName &&
    contactEmail &&
    (isNameChangeEndorsement(endorsementType)
      ? nameChangeOk
      : !!newBondAmount && !!endorsementFile)
  );
}

/**
 * Internal/super user may POST endorsement then open rider route; otherwise navigate only.
 * @param {string} role
 * @param {Parameters<typeof buildEndorsementPayloadFromState>[0]} endorsementState
 * @returns
 *   {{ mode: "none" } |
 *    { mode: "navigate", riderUrl: string } |
 *    { mode: "post_then_navigate", riderUrl: string, payload: ReturnType<typeof buildEndorsementPayloadFromState> }}
 */
export function getReleaseRiderFlow(role, endorsementState) {
  const { selectedBondToEndorse } = endorsementState;
  if (!selectedBondToEndorse) return { mode: "none" };

  const riderUrl = getReleaseRiderUrl(
    selectedBondToEndorse.bond_type,
    selectedBondToEndorse.bond_id
  );
  const isInternalOrSuper = role === "internal_user" || role === "super_user";
  const hasDetails = computeHasEndorsementDetailsForRelease(endorsementState);

  if (!isInternalOrSuper || !hasDetails) {
    const draft = buildRiderEndorsementDraftFromDialog(endorsementState);
    return {
      mode: "navigate",
      riderUrl,
      ...(draft ? { navigationState: { riderEndorsementDraft: draft } } : {}),
    };
  }
  return {
    mode: "post_then_navigate",
    riderUrl,
    payload: buildEndorsementPayloadFromState(endorsementState),
  };
}

/** Navigate to rider doc route, optionally passing dialog draft in location state. */
export function navigateToReleaseRider(navigate, riderUrl, navigationState) {
  if (!riderUrl) return;
  if (navigationState) {
    navigate(riderUrl, { state: navigationState });
  } else {
    navigate(riderUrl);
  }
}

/**
 * Runs Release Rider flow from {@link getReleaseRiderFlow} (shared by approve, law firm, archives).
 */
export async function executeReleaseRiderFlow(flow, deps) {
  const {
    navigate,
    toast,
    postEndorsementFormData,
    setIsSubmittingEndorsement,
    setEndorsementDialogOpen,
    refreshData,
  } = deps;

  if (flow.mode === "none") return;
  if (flow.mode === "blocked") {
    toast.error(flow.toastMessage);
    return;
  }
  if (flow.mode === "navigate") {
    navigateToReleaseRider(navigate, flow.riderUrl, flow.navigationState);
    return;
  }
  await postEndorsementFormData({
    payload: flow.payload,
    setSubmitting: setIsSubmittingEndorsement,
    onSuccess: async () => {
      setEndorsementDialogOpen(false);
      await refreshData();
      if (flow.riderUrl) navigate(flow.riderUrl);
    },
  });
}
